import type { Project, ProjectReader } from '@/api/client';
import { sortProjectsForPickers } from '@/utils/sortProjects';

export type ProgressPickerCategory = 'completed' | 'in_progress' | 'queued';

const LABELS: Record<ProgressPickerCategory, string> = {
  completed: 'Завершён',
  in_progress: 'В процессе',
  queued: 'В очереди',
};

/** Порядок секций в комбо и меню: сначала активные, внизу завершённые */
const PICKER_SECTION_ORDER: ProgressPickerCategory[] = ['in_progress', 'queued', 'completed'];

/** Категория строки чтеца в подменю прогресса (запись/правки). */
export function getReaderProgressPickerCategory(r: ProjectReader): ProgressPickerCategory {
  const pct = Math.min(100, Math.max(0, r.progress ?? 0));
  if (pct >= 100) return 'completed';
  const sessions = r.recording_sessions_count ?? 0;
  if (sessions > 0) return 'in_progress';
  if (pct > 0) return 'in_progress';
  if (r.avg_pages_per_hour != null && r.avg_pages_per_hour !== undefined) return 'in_progress';
  return 'queued';
}

/**
 * Категория проекта в комбо/меню.
 * Если задан readerId — по строке этого чтеца в проекте; иначе по агрегату по всем чтецам проекта.
 */
export function getProjectProgressPickerCategory(project: Project, readerId?: string | null): ProgressPickerCategory {
  const rid = readerId?.trim();
  if (rid) {
    const row = project.readers?.find((x) => x.id === rid);
    if (row) return getReaderProgressPickerCategory(row);
  }
  const readers = project.readers ?? [];
  if (readers.length === 0) {
    const pct = Math.min(100, Math.max(0, project.progress ?? 0));
    if (pct >= 100) return 'completed';
    if (pct > 0) return 'in_progress';
    return 'queued';
  }
  if (readers.every((r) => (r.progress ?? 0) >= 100)) return 'completed';
  const anyStarted = readers.some(
    (r) =>
      (r.recording_sessions_count ?? 0) > 0 ||
      (r.progress ?? 0) > 0 ||
      (r.avg_pages_per_hour != null && r.avg_pages_per_hour !== undefined)
  );
  return anyStarted ? 'in_progress' : 'queued';
}

export function groupProjectsForPickerSections(
  projects: Project[],
  readerId?: string | null
): { label: string; projects: Project[] }[] {
  const buckets: Record<ProgressPickerCategory, Project[]> = {
    completed: [],
    in_progress: [],
    queued: [],
  };
  for (const p of projects) {
    buckets[getProjectProgressPickerCategory(p, readerId)].push(p);
  }
  return PICKER_SECTION_ORDER.filter((k) => buckets[k].length > 0).map((k) => ({
    label: LABELS[k],
    projects: sortProjectsForPickers(buckets[k]),
  }));
}

export function groupReadersForPickerSections(readers: ProjectReader[]): { label: string; readers: ProjectReader[] }[] {
  const buckets: Record<ProgressPickerCategory, ProjectReader[]> = {
    completed: [],
    in_progress: [],
    queued: [],
  };
  for (const r of readers) {
    buckets[getReaderProgressPickerCategory(r)].push(r);
  }
  return PICKER_SECTION_ORDER.filter((k) => buckets[k].length > 0).map((k) => ({
    label: LABELS[k],
    readers: [...buckets[k]].sort((a, b) => a.name.localeCompare(b.name, 'ru', { sensitivity: 'base' })),
  }));
}
